import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { leaveFormTo } from "../../utils/navigation";
import EntityForm from "../../components/design/EntityForm";
import { formConfig, formInitialValues, FORM_TITLES } from "../../resources/forms";
import { useApp } from "../../design/AppContext";
import { apiGet, apiPostForm, currentAccount } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { jwtEncode } from "../../utils/jwt";
import { hasPermission } from "../../utils/permission";
import { projStatusKey, prioKey, PROJ_STATUS_CODE, PRIO_CODE, MEMBER_ROLE_CODE } from "../../design/domain";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Banner } from "../../components/mui";
import { InlineLoading } from "../../components/loading";
import { MdErrorOutline } from "react-icons/md";

/**
 * 新增專案（設計稿 #screen-project-new，el-pm-template_4.html L1017-L1064）
 *
 * 版型與送出／取消鈕全部由 EntityForm 提供，這一頁只負責欄位值與送出。
 * 欄位的分組（主要 2 欄 + 補充資料）在 resources/forms.js 的 formConfig("project")：
 * 專案代碼是必填卻收在補充資料裡，那個陷阱已經在 forms.js / EntityForm 處理過
 * （收合區裡有必填時 summary 不寫「沒填也可以」、沒填好就預設展開），不要改回去。
 *
 * 設計稿把代碼欄畫成 disabled，這裡刻意沒有跟：代碼由 /Project/NextCode 自動編，
 * 但舊案沿用既有代碼、或要對客戶自己的編號時還是得改得動（見 forms.js 的說明）。
 */

/**
 * ── 為什麼多一步「找人進來」（使用者原話：「看是否再新增專案的時候多一個STEP」）──
 *
 * 以前建完專案會丟到專案詳情，再請使用者自己去「成員」分頁一個一個加。
 * 而資料範圍是「所屬專案」的角色，沒被加進來就完全看不到這個專案 ——
 * 建案的人忘了加，同事就一直找不到它。所以趁建案當下順手問一次。
 *
 * ── 步驟怎麼分 ──
 *   1. 專案本身：名稱、說明。
 *   2. 找誰進來。選填 —— 不選也建得起來，之後在專案詳情照樣加得到。
 *
 * EntityForm 固定把「補充資料」（專案代碼、Git 連結）畫在最後一步，
 * 所以它會出現在第 2 步成員欄位底下，第 2 步的說明因此交代了一句。
 * 要讓它回到第 1 步得改 EntityForm（多一個「補充資料放在第幾步」的參數），
 * 那支是共用元件，不在這一頁的範圍內。
 *
 * ── 為什麼不在這一步問角色 ──
 * 一律先用「開發」加進去。建案當下逐一幫每個人選角色只會拖慢，
 * 而且使用者說過成員不需要改角色的功能；真的要換，移出再用別的角色加一次即可。
 *
 * ── 後端的限制（決定了下面 handleSubmit 為什麼長這樣）──
 *   · POST /Project 的 ReqPostProject 沒有成員欄位，只能建完再逐一 POST /Project/Member，
 *     一次一個人。
 *   · POST /Project 回的 data 是空字串，拿不到新 id，要靠 findNewId 用代碼回查。
 *   · 所以「專案建好了、人卻沒全部加進去」是真的會發生的狀態，不能 toast 一句
 *     「已建立」就當作沒事 —— 見 result 那一段。
 */

const FIELDS = formConfig("project");

/**
 * 成員欄位只存在於這一頁，不加進 forms.js 的共用設定：
 * 專案詳情的「編輯專案」用的是同一份 formConfig("project")，
 * 那裡多一格成員會變成「改個名字順便把人加進來」的第二個入口，
 * 而且那張表單的送出只打 PUT /Project，選了也存不進去。
 *
 * 值是「、」分隔的字串（EntityForm 的 MultiSelect 就是這個形狀），
 * 選項放的是畫面上的標籤，送出前再用 candidates 對回帳號。
 */
const MEMBERS_FIELD = {
  key: "__members",
  label: "專案成員",
  type: "multiselect",
  full: true,
  placeholder: "＋ 從名單選擇成員…",
};

/** 加進來的人一律先掛「開發」（理由見檔案上方）。用 domain.js 的對照，不要寫死數字 */
const DEFAULT_ROLE_LABEL = "開發";
const DEFAULT_ROLE = MEMBER_ROLE_CODE[DEFAULT_ROLE_LABEL];

/** 跟 EntityForm 的 splitNames 同一條切法：MultiSelect 的值就是照這些符號切的 */
const SEPARATORS = /[、,，;；]/;
const splitLabels = (v) =>
  String(v || "")
    .split(SEPARATORS)
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * 「之後去哪裡補」的講法全頁共用一份。
 * 專案詳情的「成員」是一顆獨立分頁（Detail.js 的 TAB_MEMBERS，網址 ?tab=members），
 * 清單底下的入口字樣是「＋ 加人進來」、每一列的是「移出專案」（DetailData.js），這裡照抄，使用者才找得到。
 * 那兩支檔案的分頁名稱或按鈕字樣改了，這裡要跟著改。
 */
const MEMBERS_TAB = "members";
const WHERE_TO_FIX = "至專案的「成員」分頁點選「＋ 新增成員」";

/**
 * 第 2 步的說明。
 *
 * · 「你自己會自動成為專案經理」是真的：ProjectService.PostProject 建完專案
 *   就把建立者寫進 project_member（Role = Manager）。所以名單裡也把自己拿掉了，
 *   否則選了自己只會撞到「該成員已存在於專案中」。
 * · 補充資料那一句是因為 EntityForm 把它固定畫在最後一步（見檔案上方）。
 */
const MEMBER_STEP_HINT =
  `可暫不選擇，建立後再新增。建立者將自動成為此專案的「專案經理」；選擇的成員角色一律預設為「${DEFAULT_ROLE_LABEL}」。` +
  "日後移除或新增成員，請至專案的「成員」分頁。" +
  "下方的專案代碼由系統自動產生，通常無須修改。";

function App() {
  const navigate = useNavigate();
  const { toast } = useApp();
  /*
   * 初值在 useState 的 initializer 裡算，不要用模組層級常數 ——
   * 開始日預設是「今天」，模組常數只會在載入頁面那一刻算一次，
   * 分頁開著跨過午夜就會停在昨天。
   */
  const [values, setValues] = useState(() => ({ ...formInitialValues(FIELDS), __members: "" }));
  const [sending, setSending] = useState(false);

  /*
   * 加成員掛的是 project.edit（ProjectController.PostProjectMember），跟建專案的
   * project.create 是兩個碼。只有 create 沒有 edit 的角色，每一位都會吃 403 ——
   * 讓他在第 2 步選了一串人、按下去才全部失敗，就是一個「假裝做得到」的步驟。
   * 這種情況直接不放第 2 步，改成單頁表單並講清楚（真正的把關仍在後端）。
   */
  const canAddMembers = useMemo(() => hasPermission("project.edit"), []);

  /**
   * 可以加的人：[{ account, label }]。label 是下拉與膠囊上的字，也是 values.__members 裡存的值。
   * 狀態分「讀取中／讀到了／讀不到」—— 三種情況下拉都可能是空的，說明文字要講得出是哪一種。
   */
  const [candidates, setCandidates] = useState([]);
  const [candState, setCandState] = useState("loading");

  /*
   * 送出流程的鎖。state 的 sending 要等下一次 render 才生效，連點兩下的第二下
   * 可能還看得到 false —— 那一下會建出第二個專案（代碼重複的話則是一個看不懂的錯誤）。
   * ref 是同步的，擋得住。
   */
  const lockRef = useRef(false);
  /*
   * 使用者在加成員的途中從側邊欄離開時，迴圈還會把剩下的人加完（async 函式不會因為
   * 元件卸載就停），但不能再 navigate 把人從別頁拉回來，結果改用 toast 報。
   * StrictMode 在開發模式會先卸載再掛載一次，所以掛載時要重新設回 true。
   */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /* 送出途中的進度：{ phase: "project" } 或 { phase: "members", done, total }；null＝沒在送 */
  const [progress, setProgress] = useState(null);
  /* POST /Project 本身失敗的原因。專案沒建出來，表單交還給使用者改 */
  const [submitError, setSubmitError] = useState("");
  /*
   * 專案建好了、但成員沒全部加進去時的結果：{ id, name, code, added, failed }。
   * 有值就把表單整張換掉 —— 專案已經存在，再按一次送出只會撞「專案代碼已被使用」。
   */
  const [result, setResult] = useState(null);
  const resultHeadRef = useRef(null);

  /*
   * 代碼由後端編（{前綴}-{年月}-{流水號}），開頁時就預填。
   *
   * 以前是 PM 自己填，結果同一個庫裡 TCM / AC50 / WEBPOS / PARTS 各種寫法都有，
   * 排序排不出時間也看不出哪個月開的案。改成系統統一編，但仍留成可編輯的
   * 一般欄位——舊案沿用既有代碼、或臨時要對客戶的編號時還是得改得動。
   *
   * 取不到（沒權限、後端還沒更新）就靜靜留空，讓使用者自己填，不要擋住建案。
   */
  useEffect(() => {
    let alive = true;
    apiGet("/Project/NextCode")
      .then((res) => {
        const code = _.get(res, "code", "");
        if (!alive || !code) return;
        // 只在使用者還沒動過代碼欄時才鋪上去，避免蓋掉他打到一半的內容
        setValues((prev) => (String(prev.code || "").trim() ? prev : { ...prev, code }));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  /*
   * 候選名單。新專案還沒有 id，這裡刻意送 projectId=0：
   * ProjectService.MemberCandidates 只拿 projectId 去排除「已經在這個專案裡的人」，
   * 0 對不到任何專案，所以回的就是全部啟用中的帳號（只有帳號與姓名）。
   * 這支掛 PermissionOnly("project")，建專案的人一定有；
   * 不要換成 /Auth/Admin/List —— 那支是 admin 專用，一般使用者會 403。
   */
  useEffect(() => {
    if (!canAddMembers) return undefined;
    let alive = true;
    apiGet("/Project/Member/Candidates", { projectId: 0 })
      .then((res) => {
        if (!alive) return;
        const arr = Array.isArray(res) ? res : _.get(res, "data_list", []) || [];
        /* 建立者會被後端自動加成專案經理，名單裡留著他只會讓他把自己再加一次而撞重複 */
        const me = String(currentAccount() || "").toLowerCase();
        const people = arr
          .map((a) => ({
            account: String(a.account || a.Account || "").trim(),
            name: String(a.name || a.Name || "").trim(),
          }))
          .filter((a) => a.account && a.account.toLowerCase() !== me);
        /*
         * 標籤要唯一，而且不能含 MultiSelect 的分隔符號：
         *   · 同名同姓時只寫名字，兩個人會變成同一個選項、送出時也對不回是哪一個帳號，
         *     所以撞名的才在後面補帳號。
         *   · 名字裡有「、」或逗號的話，選進去之後會被切成兩個不存在的人。
         */
        const clean = (s) => s.replace(new RegExp(SEPARATORS.source, "g"), " ").trim();
        const dup = _.countBy(people, (a) => clean(a.name));
        setCandidates(
          people.map((a) => {
            const base = clean(a.name) || a.account;
            return { account: a.account, label: dup[clean(a.name)] > 1 ? `${base}（${a.account}）` : base };
          })
        );
        setCandState("ready");
      })
      /* 不能整個吞掉：吞掉之後畫面跟「沒有人可以加」長得一模一樣 */
      .catch(() => {
        if (alive) setCandState("failed");
      });
    return () => {
      alive = false;
    };
  }, [canAddMembers]);

  const fields = useMemo(() => {
    if (!canAddMembers) return FIELDS;
    /* 下拉是空的三種原因要分開講，不然使用者只會看到一個「選不出東西」的下拉 */
    let hint = "請從名單逐一選擇，已選擇的成員會列於下方；如選錯請點選姓名旁的「移除」。名單不含您本人，您將自動加入專案。";
    if (candState === "loading") hint = <InlineLoading text="名單載入中，請稍候。" />;
    else if (candState === "failed") {
      hint = `名單載入失敗，因此選單沒有選項。可先建立專案，之後${WHERE_TO_FIX}。`;
    } else if (candidates.length === 0) hint = "系統中目前沒有其他可加入的帳號（已停用的帳號不會列出）。";
    return [...FIELDS, { ...MEMBERS_FIELD, hint, options: candidates.map((c) => c.label) }];
  }, [canAddMembers, candState, candidates]);

  /*
   * 沒有加成員權限時不傳 steps，維持原本的單頁表單：
   * 只剩一步的精靈會畫出「第 1 步／共 1 步」，那是多餘的東西（硬規則 2）。
   */
  const steps = useMemo(() => {
    if (!canAddMembers) return [];
    return [
      {
        title: "專案名稱與說明",
        hint: "名稱為必填，說明可暫不填寫。下一步再選擇專案成員。",
        keys: ["name", "description"],
      },
      { title: "專案成員", hint: MEMBER_STEP_HINT, keys: ["__members"] },
    ];
  }, [canAddMembers]);

  /** 選到的人，對回帳號。對不到的（理論上不會發生）也留著，送出時照實列為失敗，不要默默丟掉 */
  const pickedPeople = () => {
    if (!canAddMembers) return [];
    const byLabel = new Map(candidates.map((c) => [c.label, c]));
    return splitLabels(values.__members).map((label) => {
      const hit = byLabel.get(label);
      return { label, account: hit ? hit.account : "" };
    });
  };
  const pickedCount = canAddMembers ? splitLabels(values.__members).length : 0;

  /* 結果畫面出現時焦點移到它的標題：整張表單被換掉了，用鍵盤或讀螢幕的人要知道 */
  useEffect(() => {
    if (!result) return;
    window.requestAnimationFrame(() => {
      const el = resultHeadRef.current;
      if (el && el.focus) el.focus();
    });
  }, [result]);

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  /*
   * 送出途中不讓「返回／取消」直接離開：專案可能已經建好、成員加到一半，
   * 這時離開，哪幾位沒加進去就沒有地方告訴他了（按鈕在 EntityForm 裡，那兩顆送出中沒有停用）。
   */
  const back = () => {
    if (lockRef.current) {
      toast("正在建立專案並加入成員，請待處理完成後再離開。", "error");
      return;
    }
    navigate("/Project/List");
  };

  /**
   * 找出剛建好的那一筆的 id。
   *
   * 後端的 POST /Project 回的 data 是空字串（見 ProjectController.PostProject），
   * 拿不到新 id —— 但「建好之後把人加進去、直接帶去專案」都要靠它。
   * 所以建完立刻用剛送出去的代碼回查一次：代碼在這個庫裡是唯一的，
   * 而新建的 id 最大，照 id 反排抓第一頁就一定含它
   * （資料範圍受限的角色也查得到：ProjectList 會放行 CreateId 是自己的專案）。
   *
   * 回查本身失敗不算建立失敗（專案已經建好了），呼叫端要照實處理。
   */
  const findNewId = async (code) => {
    // 查詢條件走 ?Data=<JWT>，與 services/Project.js 的 GET_ProjectList 同一個形狀
    const res = await apiGet("/Project/List", {
      Data: jwtEncode({ Page: 1, PageCount: 20, OrderKey: "id", OrderDirect: "DESC" }),
    });
    // 回傳可能是 { data_list, total_count } 也可能是純陣列，兩種都吃
    const raw = Array.isArray(res) ? res : _.get(res, "data_list", []);
    const rows = Array.isArray(raw) ? raw : [];
    const hit = rows.find((r) => String(r.code ?? r.Code ?? "").trim() === code);
    const id = Number(hit ? hit.id ?? hit.Id ?? 0 : 0);
    return Number.isInteger(id) && id > 0 ? id : 0;
  };

  /** 回查 id；網路錯誤一樣當成「找不到」，由呼叫端決定怎麼講 */
  const safeFindId = async (code) => {
    try {
      return await findNewId(code);
    } catch (e) {
      return 0;
    }
  };

  /**
   * 一位一位加進專案。後端 POST /Project/Member 一次只收一個人。
   *
   * 刻意逐一 await 而不是 Promise.all：每加一位，後端都會對專案全體成員發一則通知
   * （NotificationService.NotifyProject），同時打十支會同時寫十批通知、排十批信；
   * 而且逐一做才報得出「加到第幾位」。
   *
   * 「該成員已存在於專案中」算成功：那個人確實在專案裡了。會碰到的情況是
   * 後端已經寫進成員、卻在後面發通知時出錯回了 400 —— 使用者按「再試一次」
   * 就會撞到這一句，把它列成失敗只會讓他一直重試一件早就完成的事。
   */
  const addMembers = async (projectId, people) => {
    const added = [];
    const failed = [];
    for (let i = 0; i < people.length; i += 1) {
      const p = people[i];
      if (mounted.current) setProgress({ phase: "members", done: i + 1, total: people.length });
      if (!p.account) {
        failed.push({ ...p, reason: "名單中找不到此成員的帳號。", denied: false });
        continue;
      }
      try {
        // 後端 [FromForm] ReqPost 只認 data 欄位；jwtEncode 會自己補上 Exp
        // 逐一送是刻意的（見上面的說明），不是漏寫 Promise.all
        // eslint-disable-next-line no-await-in-loop
        await apiPostForm("/Project/Member", {
          data: jwtEncode({ ProjectId: projectId, Account: p.account, Role: DEFAULT_ROLE }),
        });
        added.push(p);
      } catch (e) {
        const msg = apiErrorMessage(e, "加入失敗");
        if (msg.includes("已存在於專案中")) {
          added.push(p);
        } else {
          /* 403 另外記：那是權限問題，「再試一次」不會有不同結果，就不該給那顆按鈕 */
          failed.push({ ...p, reason: msg, denied: _.get(e, "response.status") === 403 });
        }
      }
    }
    return { added, failed };
  };

  /**
   * 使用者已經離開這一頁（從側邊欄走掉）時的收尾：不 navigate、不 setState，
   * 只用 toast 把結果講出來 —— toast 掛在 Layout 上，換頁之後還在。
   * 失敗的那則停久一點，名單要讀得完。
   */
  const reportAfterLeaving = (projName, failed) => {
    if (!failed.length) {
      toast(`專案「${projName}」已建立，所選成員皆已加入`);
      return;
    }
    toast(
      `專案「${projName}」已建立，但有 ${failed.length} 位未能加入：${failed.map((f) => f.label).join("、")}。請${WHERE_TO_FIX}補加。`,
      "error",
      12000
    );
  };

  /** 專案已經存在之後的共同收尾：全部成功就帶去專案，有人沒加進去就留在這一頁照實講 */
  const settle = (id, name, code, added, failed) => {
    if (!mounted.current) {
      reportAfterLeaving(name, failed);
      return;
    }
    if (id && failed.length === 0) {
      toast(added.length ? `已建立專案，並加入 ${added.length} 位成員` : "已建立專案");
      /* 建好就直接進專案詳情（設計稿 L1061「不會掉回列表」）；表單步驟從歷史裡退掉，返回才不會掉回表單 */
      leaveFormTo(navigate, `/Project/Detail/${id}`);
      return;
    }
    setResult({ id, name, code, added, failed });
    setProgress(null);
    setSending(false);
    lockRef.current = false;
  };

  const handleSubmit = async () => {
    if (lockRef.current) return;

    // 與後端 ReqPostProjectValidator 同步：代碼與名稱為必填
    const code = String(values.code || "").trim();
    const name = String(values.name || "").trim();
    if (!code) {
      toast("專案代碼不可為空", "error");
      return;
    }
    if (!name) {
      toast("專案名稱不可為空", "error");
      return;
    }

    // 下拉存的是中文標籤，送出前轉回後端 enum 數字
    const payload = {
      Code: code,
      Name: name,
      Description: values.description || "",
      ClientName: values.client_name || "",
      Status: PROJ_STATUS_CODE[projStatusKey(values.status || "規劃中")],
      Priority: PRIO_CODE[prioKey(values.priority || "一般")],
      // 不送起訖日：專案不管時間，畫面上顯示的是由工項推導出來的區間（見 forms.js）
      StartDate: "",
      EndDate: "",
      GitUrl: values.git_url || "",
    };
    /* 在送出的這一刻就把名單定下來，後面的每一步都用這一份 */
    const people = pickedPeople();

    /*
     * 送出中要擋住第二次點擊：建立專案會同步寫活動紀錄與通知，慢的時候要好幾秒，
     * 畫面沒有任何變化的話使用者一定會再按一次 —— 第二次會撞到「專案代碼重複」。
     */
    lockRef.current = true;
    setSending(true);
    setSubmitError("");
    setProgress({ phase: "project" });

    try {
      // 後端 [FromForm] ReqPost 只認 data 欄位，內容需為 JWT 封裝的 payload
      await apiPostForm("/Project", { data: jwtEncode(payload) });
    } catch (e) {
      // 後端把失敗原因放在 res_msg（例如「新增失敗: 專案代碼「…」已被使用」），優先顯示它
      const msg = apiErrorMessage(e, "建立專案失敗");
      toast(msg, "error");
      if (mounted.current) {
        /* toast 幾秒後就沒了，同一句話再留一份在畫面上（硬規則 4） */
        setSubmitError(msg);
        setProgress(null);
        setSending(false);
      }
      lockRef.current = false;
      return;
    }

    /* ── 從這裡開始專案已經存在：不管後面怎樣，都不能把一張「可以再送一次」的表單交還給他 ── */

    const id = await safeFindId(code);
    if (!id) {
      /*
       * 找不到 id（極少數：回查那一支剛好失敗）。專案其實建好了。
       * 沒選人就退回列表 —— 至少不要停在一張已經送出去的表單上；
       * 選了人的話那些人一個都還沒加，要留在這一頁照實講，不能帶著一句「已建立」就走。
       */
      if (!people.length) {
        toast("已建立專案，但暫時無法取得專案編號，將返回專案列表");
        if (mounted.current) navigate("/Project/List");
        return;
      }
      settle(0, name, code, [], people.map((p) => ({ ...p, reason: "", denied: false })));
      return;
    }

    const { added, failed } = people.length ? await addMembers(id, people) : { added: [], failed: [] };
    settle(id, name, code, added, failed);
  };

  /**
   * 結果畫面上的「再試一次」。只重做沒成功的部分：
   * 還沒有 id 就先再找一次 id，再把剩下的人加進去。專案不會再建一次。
   */
  const retry = async () => {
    if (lockRef.current || !result) return;
    lockRef.current = true;
    setSending(true);

    let { id } = result;
    if (!id) {
      setProgress({ phase: "find" });
      id = await safeFindId(result.code);
    }
    if (!id) {
      if (!mounted.current) {
        reportAfterLeaving(result.name, result.failed);
        return;
      }
      toast("仍無法取得此專案的編號，請至專案列表開啟該專案後再新增成員。", "error");
      setProgress(null);
      setSending(false);
      lockRef.current = false;
      return;
    }
    const { added, failed } = await addMembers(id, result.failed);
    settle(id, result.name, result.code, [...result.added, ...added], failed);
  };

  /* ================= 專案已建立、但有人沒加進去 ================= */

  if (result) {
    const { id, name, code, added, failed } = result;
    /* 全部都是權限不足時，「再試一次」不會有不同結果，那顆按鈕就不畫 */
    const retryable = !id || failed.some((f) => !f.denied);
    /* banner 裡引用的是按鈕平常的字；送出中那幾秒按鈕會換成「處理中…」，banner 不跟著換 */
    const retryLabel = id ? `重新加入這 ${failed.length} 位` : "重新查詢專案並加入成員";
    /* 已經講了「進去之後」，就不必再說一次「到專案的」 */
    const whereInside = WHERE_TO_FIX.replace(/^至專案的/, "至");

    return (
      <Box component="section" className="pg">
        {/* tabIndex -1：表單被換掉之後的焦點落點，不進 Tab 順序 */}
        <Typography variant="h1" component="h1" tabIndex={-1} ref={resultHeadRef}>
          專案已建立
        </Typography>
        <Typography sx={{ mt: "var(--space-3)" }}>「{name}」（代碼 {code}）已建立，無須重複建立。</Typography>

        <Stack spacing={2} sx={{ mt: "var(--space-6)" }}>
          {/* 重要後果寫成橫幅，不是灰字小字（硬規則 4） */}
          <Banner kind="attention" role="alert" icon={<MdErrorOutline />}>
            {id ? (
              <span>
                <strong>有 {failed.length} 位未能加入專案　</strong>
                各成員未能加入的原因列於下方。如需補加，請{WHERE_TO_FIX}。
              </span>
            ) : (
              <span>
                <strong>所選的 {failed.length} 位尚未加入專案　</strong>
                專案已建立，但系統暫時無法取得專案編號，因此尚無法加入成員。
                請點選「{retryLabel}」，或至專案列表開啟「{name}」後{whereInside}。
              </span>
            )}
          </Banner>

          {/* 這幾列沒有動作，用不可點的清單列，不要讓人以為點得下去 */}
          <Card>
            <List disablePadding>
              {failed.map((f) => (
                <ListItem key={f.label} divider sx={{ px: 3, py: 2, flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
                  <Typography variant="subtitle1">{f.label}</Typography>
                  {/* 找不到 id 的情況下每一位的原因都一樣，已經寫在上面的 banner 裡，不重複 */}
                  {f.reason && (
                    <Typography variant="body2" color="text.secondary">
                      {f.reason}
                    </Typography>
                  )}
                </ListItem>
              ))}
            </List>
          </Card>

          {/* 已經成功的人也要講：不然使用者會以為要把整串人重加一次（重加會撞「已存在」） */}
          {added.length > 0 && (
            <Typography>
              已加入的 {added.length} 位：{added.map((a) => a.label).join("、")}。
            </Typography>
          )}

          {/* 送「再試一次」的途中顯示進度，理由同表單底下那一條 */}
          {progress && (
            <Banner kind="info" role="status">
              {progress.phase === "members"
                ? `正在逐一加入成員（第 ${progress.done}／${progress.total} 位），請勿關閉或重新整理此頁面。`
                : "正在查詢專案編號…"}
            </Banner>
          )}

          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
            {/* 整頁唯一的主要按鈕：去能補人的地方 */}
            <Button
              variant="contained"
              disabled={sending}
              onClick={() => navigate(id ? `/Project/Detail/${id}?tab=${MEMBERS_TAB}` : "/Project/List")}
            >
              {id ? "前往專案補加成員" : "前往專案列表"}
            </Button>
            {retryable && (
              <Button variant="outlined" disabled={sending} loading={sending} loadingPosition="start" onClick={retry}>
                {sending ? "處理中…" : retryLabel}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    );
  }

  /* ================= 表單 ================= */

  /* 主鈕寫出按下去會發生什麼事：有選人就連人數一起講 */
  let submitText = "建立並檢視專案";
  if (pickedCount > 0) submitText = `建立專案並加入 ${pickedCount} 位成員`;

  return (
    <>
      {/*
        沒有加成員權限：第 2 步不放（理由見 canAddMembers），但後果要講 ——
        資料範圍是「所屬專案」的同事，沒被加進來就看不到這個專案（硬規則 4）。
      */}
      {!canAddMembers && (
        <Banner kind="info" sx={{ mb: "var(--space-4)" }}>
          <strong>此處無法同時加入成員　</strong>
          您的帳號沒有「編輯專案」權限，新增成員需具備此權限。專案建立後，請聯絡系統管理員協助加入成員；
          未加入專案的使用者，可能無法檢視此專案。
        </Banner>
      )}
      <EntityForm
        title={FORM_TITLES.project}
        subtitle={
          canAddMembers
            ? "請先填寫名稱，下一步再選擇成員；工項請於專案建立後新增。"
            : "請先填寫名稱，工項請於專案建立後新增。"
        }
        fields={fields}
        /* 只有這個入口打開分步驟：專案詳情的「編輯專案」不傳 steps，改一格名字不該走完整個精靈 */
        steps={steps}
        values={values}
        onChange={handleChange}
        onBack={back}
        onSubmit={handleSubmit}
        submitText={submitText}
        busy={sending}
      />
      {/*
        送出途中的進度。建專案＋逐一加人，選了十個人就是十幾支請求，
        主鈕只寫「送出中…」的話，使用者分不出卡在哪裡，也不知道現在關掉會怎樣。
        「先別關掉」是真的：迴圈跑在這個分頁裡，關掉或重新整理之後，還沒輪到的人就不會加。
      */}
      {progress && (
        <Banner kind="info" role="status" sx={{ mt: "var(--space-4)" }}>
          {progress.phase === "members"
            ? `專案已建立，正在逐一加入成員（第 ${progress.done}／${progress.total} 位）。請勿關閉或重新整理此頁面，否則尚未處理的成員將不會加入。`
            : "正在建立專案…"}
        </Banner>
      )}
      {/*
        專案本身沒建成功：toast 已經講過一次，但它幾秒後就消失。
        同一句話留一份在按鈕下方，並且講清楚「填的東西還在」，不然他只會重刷一次頁面。
      */}
      {submitError && (
        <Banner kind="attention" role="alert" icon={<MdErrorOutline />} sx={{ mt: "var(--space-4)" }}>
          <span>
            <strong>專案建立失敗　</strong>
            {submitError}
            {/* 分步驟時名稱與說明在第 1 步，要講出回得去的路；單頁表單沒有「上一步」這顆鈕，就不提 */}
            {canAddMembers
              ? `　已填寫的內容與所選成員仍保留。專案代碼位於本步驟下方，如需修改名稱請按「上一步」；修改後再按一次「${submitText}」。`
              : `　已填寫的內容仍保留，修改後再按一次「${submitText}」。`}
          </span>
        </Banner>
      )}
      {/*
        「接下來會發生什麼事」寫在卡片底下（設計稿 L1061）。
        EntityForm 沒有卡片內的註腳插槽，所以接在卡片後面。
        原本這裡跟著卡片寫死 max-width:640px，但 EntityForm 已經拿掉卡片的寬度上限
        （見那裡的說明），再留著這句話就會跟卡片對不齊，所以一起拿掉。

        通知照實寫（查過後端）：ProjectService.PostProjectMember 每加一位就呼叫一次
        NotificationService.NotifyProject —— 收件人是「專案目前全部成員扣掉操作的人」，
        而那一位在發通知前已經寫進 project_member，所以他自己也收得到；
        通知會落地（通知中心看得到），信則依各人的偏好排進佇列
        （歸在「工項指派」那一類，沒設定過＝預設要寄）。
        所以不能寫「只會通知被加進來的人」，也不能保證一定有信。
        建專案本身（PostProject）當下成員只有建立者自己，扣掉之後不會發出任何通知。
      */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: "var(--space-4)" }}>
        {canAddMembers
          ? "成員全部加入後將直接開啟此專案；若有成員未能加入，將停留於此頁並列出未加入者與補加方式。每加入一位成員，專案中除您以外的成員（包含剛加入者）皆會於通知中心收到一則「新增專案成員」通知，您本人不會收到；是否另寄電子郵件依各人的通知設定而定。"
          : "建立後將直接開啟此專案，不會返回列表。"}
      </Typography>
    </>
  );
}

App.propTypes = {};
App.defaultProps = {};

export default App;
